import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const FORM_NAME = "FORM60";
const PDF_ENDPOINT = "/FillMyForm/SBI_FORM60/Print.php";

const GROUPS = [
  {
    "title": "Applicant Personal Details",
    "fields": [
      {
        "name": "afname",
        "label": "Applicant First Name",
        "type": "text",
        "required": true,
        "maxLength": 25,
        "alphaOnly": true
      },
      {
        "name": "amname",
        "label": "Applicant Middle Name",
        "type": "text",
        "required": false,
        "maxLength": 25,
        "alphaOnly": true
      },
      {
        "name": "alname",
        "label": "Applicant Last Name",
        "type": "text",
        "required": true,
        "maxLength": 25,
        "alphaOnly": true
      },
      {
        "name": "dob",
        "label": "Date of Birth",
        "type": "date",
        "required": true
      },
      {
        "name": "ffname",
        "label": "Father's First Name",
        "type": "text",
        "required": true,
        "maxLength": 25,
        "alphaOnly": true
      },
      {
        "name": "fmname",
        "label": "Father's Middle Name",
        "type": "text",
        "required": false,
        "maxLength": 25,
        "alphaOnly": true
      },
      {
        "name": "flname",
        "label": "Father's Last Name",
        "type": "text",
        "required": true,
        "maxLength": 25,
        "alphaOnly": true
      },
      {
        "name": "mobile",
        "label": "Mobile Number",
        "type": "text",
        "required": true,
        "maxLength": 10,
        "numeric": true
      },
      {
        "name": "aadhaar",
        "label": "Aadhaar Number",
        "type": "text",
        "required": true,
        "maxLength": 12,
        "numeric": true
      }
    ]
  },
  {
    "title": "Applicant Address Details",
    "fields": [
      {
        "name": "add1",
        "label": "Address Line 1",
        "type": "text",
        "required": true,
        "maxLength": 20
      },
      {
        "name": "add2",
        "label": "Address Line 2",
        "type": "text",
        "required": true,
        "maxLength": 25
      },
      {
        "name": "add3",
        "label": "Address Line 3",
        "type": "text",
        "required": false,
        "maxLength": 20
      },
      {
        "name": "add4",
        "label": "Address Line 4",
        "type": "text",
        "required": false,
        "maxLength": 25
      },
      {
        "name": "add5",
        "label": "Address Line 5",
        "type": "text",
        "required": false,
        "maxLength": 20
      },
      {
        "name": "add6",
        "label": "Address Line 6",
        "type": "text",
        "required": false,
        "maxLength": 25
      },
      {
        "name": "city",
        "label": "City",
        "type": "text",
        "required": true,
        "maxLength": 12
      },
      {
        "name": "dist",
        "label": "District",
        "type": "text",
        "required": true,
        "maxLength": 21
      },
      {
        "name": "state",
        "label": "State",
        "type": "text",
        "required": true,
        "maxLength": 14
      },
      {
        "name": "pin",
        "label": "PIN",
        "type": "text",
        "required": true,
        "maxLength": 6,
        "numeric": true
      }
    ]
  }
];

const allFields = GROUPS.flatMap((group) => group.fields);

const initialFormData = Object.fromEntries(
  allFields.map((field) => [field.name, ""]),
);

function formatDate(value) {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return year && month && day
    ? `${day}/${month}/${year}`
    : value;
}

async function readJson(response) {
  const text = await response.text();

  if (!text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      "The server returned an invalid JSON response.",
    );
  }
}

export default function SBI_FORM60() {
  const [formData, setFormData] =
    useState(initialFormData);

  const [errors, setErrors] =
    useState({});

  const [currentTab, setCurrentTab] =
    useState(0);

  const [maxAllowedTab, setMaxAllowedTab] =
    useState(0);

  const [pdfUrl, setPdfUrl] =
    useState("");

  const [showPdf, setShowPdf] =
    useState(false);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [pdfError, setPdfError] =
    useState("");

  const nextButtonRef = useRef(null);

  const activeGroup =
    currentTab < GROUPS.length
      ? GROUPS[currentTab]
      : null;

  const currentFields =
    activeGroup?.fields || [];

  useEffect(() => {
    if (!activeGroup) {
      return undefined;
    }

    const firstField =
      currentFields[0];

    if (!firstField) {
      return undefined;
    }

    const frame =
      window.requestAnimationFrame(() => {
        document
          .getElementById(firstField.name)
          ?.focus();
      });

    return () =>
      window.cancelAnimationFrame(frame);
  }, [currentTab]);

  useEffect(() => {
    const controller =
      new AbortController();

    (async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(
            FORM_NAME,
          )}`,
          {
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        const result =
          await readJson(response);

        if (
          !response.ok ||
          !result?.success ||
          !result?.found ||
          !result?.data
        ) {
          return;
        }

        const data = result.data;

        setFormData((previous) => ({
          ...previous,
          city:
            previous.city ||
            data.city ||
            "",
          dist:
            previous.dist ||
            data.dist ||
            "",
          state:
            previous.state ||
            data.state ||
            "",
          pin:
            previous.pin ||
            data.pin ||
            "",
        }));
      } catch (error) {
        if (
          error.name !== "AbortError"
        ) {
          console.error(
            "SBI_FORM60 autofill error:",
            error,
          );
        }
      }
    })();

    return () => controller.abort();
  }, []);

  useEffect(
    () => () => {
      if (
        pdfUrl?.startsWith("blob:")
      ) {
        URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl],
  );

  const updateField = (
    field,
    rawValue,
  ) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    setFormData((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));
  };

  const validateField = (field) => {
    const value = String(
      formData[field.name] ?? "",
    ).trim();

    if (
      field.required &&
      !value
    ) {
      return `${field.label} is required.`;
    }

    if (
      field.alphaOnly &&
      value &&
      !/^[A-Za-z ]+$/.test(value)
    ) {
      return `${field.label} must contain alphabet letters only.`;
    }

    if (
      field.name === "mobile" &&
      value &&
      !/^\d{10}$/.test(value)
    ) {
      return "Mobile number must contain 10 digits.";
    }

    if (
      field.name === "aadhaar" &&
      value &&
      !/^\d{12}$/.test(value)
    ) {
      return "Aadhaar number must contain exactly 12 digits.";
    }

    if (
      field.name === "pin" &&
      value &&
      !/^\d{6}$/.test(value)
    ) {
      return "PIN must contain exactly 6 digits.";
    }

    if (
      field.name === "dob" &&
      value &&
      new Date(`${value}T00:00:00`) >
        new Date()
    ) {
      return "Date of birth cannot be in the future.";
    }

    return "";
  };

  const validateFields = (fields) => {
    const nextErrors = {};

    fields.forEach((field) => {
      const message =
        validateField(field);

      if (message) {
        nextErrors[field.name] =
          message;
      }
    });

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  const goNext = () => {
    if (
      !validateFields(currentFields)
    ) {
      return;
    }

    const nextTab =
      currentTab + 1;

    setCurrentTab(nextTab);
    setMaxAllowedTab((previous) =>
      Math.max(previous, nextTab),
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goPrevious = () => {
    setCurrentTab((previous) =>
      Math.max(0, previous - 1),
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleLastFieldKeyDown = (
    event,
    fieldName,
  ) => {
    const lastFieldName =
      currentFields[
        currentFields.length - 1
      ]?.name;

    if (
      fieldName === lastFieldName &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();

    if (!validateFields(allFields)) {
      const firstInvalidField =
        allFields.find((field) =>
          validateField(field),
        );

      const invalidTab =
        GROUPS.findIndex((group) =>
          group.fields.some(
            (field) =>
              field.name ===
              firstInvalidField?.name,
          ),
        );

      if (invalidTab >= 0) {
        setCurrentTab(invalidTab);
      }

      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      Object.entries(formData).forEach(
        ([name, value]) => {
          body.append(
            name,
            String(value ?? ""),
          );
        },
      );

      const controller =
        new AbortController();

      const timeoutId =
        window.setTimeout(
          () =>
            controller.abort(),
          60000,
        );

      let response;

      try {
        response = await fetch(
          PDF_ENDPOINT,
          {
            method: "POST",
            body,
            credentials: "include",
            headers: {
              Accept:
                "application/pdf",
            },
            signal:
              controller.signal,
          },
        );
      } finally {
        window.clearTimeout(
          timeoutId,
        );
      }

      if (!response.ok) {
        throw new Error(
          (await response.text()) ||
            `PDF generation failed (${response.status}).`,
        );
      }

      const contentType =
        response.headers.get(
          "content-type",
        ) || "";

      if (
        !contentType.includes(
          "application/pdf",
        )
      ) {
        throw new Error(
          (await response.text()) ||
            "Print.php did not return a PDF file.",
        );
      }

      const objectUrl =
        URL.createObjectURL(
          await response.blob(),
        );

      setPdfUrl((previous) => {
        if (
          previous?.startsWith(
            "blob:",
          )
        ) {
          URL.revokeObjectURL(
            previous,
          );
        }

        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      setPdfError(
        error.name === "AbortError"
          ? "PDF generation timed out."
          : error.message ||
              "Unable to generate PDF preview.",
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error =
      errors[field.name];

    return (
      <div
        key={field.name}
        className="space-y-2"
      >
        <Label htmlFor={field.name}>
          {field.label}

          {field.required && (
            <span className="text-red-500">
              {" "}
              *
            </span>
          )}
        </Label>

        <Input
          id={field.name}
          name={field.name}
          type={
            field.type || "text"
          }
          value={
            formData[field.name]
          }
          maxLength={
            field.maxLength
          }
          placeholder={field.label}
          className={
            error
              ? "border-red-500"
              : ""
          }
          onChange={(event) =>
            updateField(
              field,
              event.target.value,
            )
          }
          onKeyDown={(event) =>
            handleLastFieldKeyDown(
              event,
              field.name,
            )
          }
        />

        {error && (
          <p className="text-sm text-red-500">
            {error}
          </p>
        )}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI Form 60"
        onBack={() =>
          setShowPdf(false)
        }
      />
    );
  }

  const isPreviewTab =
    currentTab === GROUPS.length;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI Form 60
            </CardTitle>

            <p className="text-muted-foreground">
              Enter the applicant and address
              details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-3 gap-3">
          {[
            ...GROUPS.map(
              (group) => group.title,
            ),
            "Preview",
          ].map((title, index) => (
            <Button
              key={title}
              type="button"
              variant={
                currentTab === index
                  ? "default"
                  : "outline"
              }
              disabled={
                index >
                maxAllowedTab
              }
              onClick={() =>
                setCurrentTab(index)
              }
            >
              {index + 1}. {title}
            </Button>
          ))}
        </div>

        {!isPreviewTab ? (
          <Card>
            <CardHeader>
              <CardTitle>
                {activeGroup.title}
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {currentFields.map(
                  renderField,
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                📋 Preview
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-8">
              {GROUPS.map((group) => (
                <section
                  key={group.title}
                >
                  <h3 className="mb-3 text-lg font-semibold">
                    {group.title}
                  </h3>

                  <div className="overflow-hidden rounded-lg border">
                    {group.fields.map(
                      (field) => (
                        <div
                          key={field.name}
                          className="grid grid-cols-2 border-b p-3 last:border-b-0"
                        >
                          <span className="font-medium">
                            {field.label}
                          </span>

                          <span className="break-words">
                            {field.type ===
                            "date"
                              ? formatDate(
                                  formData[
                                    field
                                      .name
                                  ],
                                )
                              : formData[
                                  field
                                    .name
                                ] || "-"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </section>
              ))}
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={
              currentTab === 0
            }
          >
            ← Previous
          </Button>

          {isPreviewTab ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading
                ? "Generating PDF..."
                : "Submit & Open PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={goNext}
            >
              {currentTab ===
              GROUPS.length - 1
                ? "Preview"
                : "Next →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
